import React, { useRef, useState, useEffect, useMemo } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import { useNavigate } from 'react-router-dom';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import './Services.css';
import { useAirtable } from '../../hooks/useAirtable';
import { servicesAPI, testimonialsAPI, contactsAPI } from '../../services/airtable';

const SkeletonCard = () => (
  <div className="member-card skeleton">
    <div className="member-avatar skeleton-avatar" />
    <div className="member-info">
      <div className="member-name skeleton-line" style={{ width: '60%' }} />
      <div className="member-role skeleton-line" style={{ width: '40%' }} />
    </div>
  </div>
);

const Services = () => {
  const { data: services, loading: servicesLoading, error: servicesError } = useAirtable(servicesAPI.getAll, false);
  const { data: contacts, loading: contactsLoading } = useAirtable(contactsAPI.getAll, false);
  const { data: testimonials, loading: tLoading, error: tError } = useAirtable(testimonialsAPI.getAll, false);
  const sectionRef = useRef(null);
  const navigate = useNavigate();

  // CHANGE: Initialize with null instead of -1 (null means "All Members" / "Select a Service")
  const [selectedServiceId, setSelectedServiceId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [displayCount, setDisplayCount] = useState(12);
  const [memberStatusFilter, setMemberStatusFilter] = useState('');

  // Get services data
  const servicesList = useMemo(
    () => servicesLoading ? [] : Array.isArray(services) ? services : (services?.records || []),
    [services, servicesLoading]
  );
  
  const contactsList = useMemo(
    () => contactsLoading ? [] : Array.isArray(contacts) ? contacts : (contacts?.records || []),
    [contacts, contactsLoading]
  );

  // CHANGE: Get the selected service object
  const selectedService = useMemo(() => {
    if (selectedServiceId === null || !servicesList.length) return null;
    return servicesList.find((_, idx) => idx === selectedServiceId);
  }, [selectedServiceId, servicesList]);

  // CHANGE: Get contacts based on selected service (null = show ALL members)
  const getServiceContacts = useMemo(() => {
    // If no service selected, show ALL members
    if (selectedServiceId === null || !selectedService) {
      return contactsList;
    }

    if (!servicesList.length || !contactsList.length) return [];

    // Get the People field from selected service
    const peopleField = selectedService.People;

    console.log('🔍 Selected Service:', selectedService.Name);
    console.log('👥 People IDs from Service:', peopleField);

    if (!peopleField || (Array.isArray(peopleField) && peopleField.length === 0)) {
      console.warn('⚠️ No people linked to this service');
      return [];
    }

    // Handle different formats - People field contains record IDs
    const linkedIds = Array.isArray(peopleField) ? peopleField : [peopleField];

    console.log('🔗 Matching Member IDs:', linkedIds);

    // Filter members by matching their ID with the linked IDs
    const matches = contactsList.filter(member => {
      const isMatched = linkedIds.includes(member.id);
      if (isMatched) {
        console.log('✅ Found member:', member.Name, '| ID:', member.id);
      }
      return isMatched;
    });

    console.log('✨ Total members matched:', matches.length);
    return matches;
  }, [selectedServiceId, selectedService, servicesList, contactsList]);

  // Apply search filter - search both members and services
  const searchedContacts = useMemo(() => {
    if (!searchTerm) {
      return getServiceContacts;
    }

    const lowerSearch = searchTerm.toLowerCase();

    // If a specific service is selected, just filter members
    if (selectedServiceId !== null && selectedService) {
      return getServiceContacts.filter(contact =>
        (contact.Name?.toLowerCase().includes(lowerSearch)) ||
        (contact.Email?.toLowerCase().includes(lowerSearch)) ||
        ((contact.Availability || '').toLowerCase().includes(lowerSearch)) ||
        (contact.Title?.toLowerCase().includes(lowerSearch))
      );
    }

    // "All Members" mode: search both services AND members
    // Check if search term matches any service name
    const matchingServices = servicesList.filter(service =>
      service.Name?.toLowerCase().includes(lowerSearch)
    );

    // If a service matches, show members from that service
    if (matchingServices.length > 0) {
      const allMembers = [];
      matchingServices.forEach(service => {
        const peopleField = service.People;
        if (peopleField) {
          const linkedIds = Array.isArray(peopleField) ? peopleField : [peopleField];
          const matches = contactsList.filter(member => linkedIds.includes(member.id));
          allMembers.push(...matches);
        }
      });
      // Remove duplicates
      return [...new Map(allMembers.map(m => [m.id, m])).values()];
    }

    // Also search for members by name, email, title, or availability
    const memberMatches = getServiceContacts.filter(contact =>
      (contact.Name?.toLowerCase().includes(lowerSearch)) ||
      (contact.Email?.toLowerCase().includes(lowerSearch)) ||
      ((contact.Availability || '').toLowerCase().includes(lowerSearch)) ||
      (contact.Title?.toLowerCase().includes(lowerSearch))
    );

    return memberMatches;
  }, [searchTerm, getServiceContacts, selectedServiceId, selectedService, servicesList, contactsList]);

  // Filter by member status and exclude "not available"
  const filteredForAvailability = useMemo(() => {
    let filtered = searchedContacts;

    // Always exclude "not available" members
    filtered = filtered.filter(contact => {
      const availability = (contact.Availability || '').toLowerCase().trim();
      return availability !== 'not available' && availability !== 'unavailable' && availability !== 'no';
    });

    // Apply member status filter if selected
    if (memberStatusFilter) {
      filtered = filtered.filter(contact => {
        const availability = (contact.Availability || '').toLowerCase().trim();
        const filterLower = memberStatusFilter.toLowerCase().trim();
        return availability === filterLower;
      });
    }

    return filtered;
  }, [searchedContacts, memberStatusFilter]);

  // Apply pagination
  const paginatedContacts = filteredForAvailability.slice(0, displayCount);

  // CHANGE: Reset display count when service changes
  useEffect(() => {
    setDisplayCount(12);
    setSearchTerm('');
  }, [selectedServiceId]);

  // Get the description text for the header
  const getDescriptionText = () => {
    if (servicesLoading) return 'Loading services...';
    if (selectedServiceId === null || !selectedService) {
      return 'Meet our entire team of talented professionals';
    }
    return selectedService.Description || selectedService.description || 'No description available';
  };

  return (
    <section className="services-section" ref={sectionRef}>
      {/* Blurred background layer */}
      <div
        className="services-bg-blur"
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL}/images/hero/hero-background.jpg)`,
          height: 'calc(100vh + 200px)',
          top: '-100px'
        }}
      />

      <div className="services-header-section">
        <h2 className="services-title">The Community & Services</h2>

        {/* Service Description */}
        {!servicesLoading && (
          <p className="services-subheader-text">
            {getDescriptionText()}
          </p>
        )}
      </div>

      {servicesError && (
        <div className="services-error" role="alert">
          <strong>Error loading services:</strong> {servicesError}
        </div>
      )}

      {/* 3-Column Layout */}
      <div className="services-layout">
        {/* Left Sidebar */}
        <aside className="services-sidebar">
          {/* Search Bar */}
          <div className="service-search-container">
            <input
              type="text"
              className="service-search-input"
              placeholder="Search members or services..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Member Status Filter Dropdown */}
          <div className="member-status-filter-container">
            {contactsLoading ? (
              <div className="dropdown-skeleton" />
            ) : (
              <select
                className="member-status-dropdown"
                value={memberStatusFilter}
                onChange={(e) => {
                  setMemberStatusFilter(e.target.value);
                  setSearchTerm('');
                  setDisplayCount(12);
                }}
                aria-label="Filter by member status"
              >
                <option value="">👨🏻‍💻👩🏻‍💻 All Members</option>
                <option value="available for work">Available for work</option>
                <option value="part time">Part Time</option>
                <option value="full load">Full Load</option>
              </select>
            )}
          </div>

          {/* CHANGE: Services Dropdown - Fixed to show all by default */}
          <div className="services-dropdown-container">
            {servicesLoading ? (
              <div className="dropdown-skeleton" />
            ) : (
              <select
                className="services-dropdown"
                value={selectedServiceId === null ? "" : selectedServiceId}
                onChange={(e) => {
                  const value = e.target.value;
                  // If empty string, set to null (show all members)
                  if (value === "") {
                    setSelectedServiceId(null);
                  } else {
                    setSelectedServiceId(parseInt(value));
                  }
                  setSearchTerm('');
                  setDisplayCount(12);
                }}
                aria-label="Select a service"
              >
                {/* CHANGE: Default option that shows ALL members */}
                <option value="">📋 All Services</option>
                {servicesList.map((service, idx) => (
                  <option key={service.id || idx} value={idx}>
                    {service.Name}
                  </option>
                ))}
              </select>
            )}
          </div>
        </aside>

        {/* Center Content: Members Grid */}
        <main className="services-main">
          {/* Debug Info */}
          {!contactsLoading && contactsList.length === 0 && (
            <div className="services-error" role="alert">
              ⚠️ No members loaded from Members table. Check Airtable connection.
            </div>
          )}

          {/* Team Members Grid */}
          <div className="members-grid-container">
            <div className="members-grid">
              {contactsLoading ? (
                Array.from({ length: 12 }).map((_, i) => (
                  <SkeletonCard key={`skeleton-${i}`} />
                ))
              ) : paginatedContacts.length === 0 ? (
                <div className="no-members-message">
                  {contactsList.length === 0
                    ? 'No members available'
                    : searchTerm
                    ? `No members match "${searchTerm}"`
                    : selectedServiceId === null
                    ? 'No members available'
                    : `No members in "${selectedService?.Name || 'this service'}"`
                  }
                </div>
              ) : (
                paginatedContacts.map(contact => {
                  const imageUrl = Array.isArray(contact.Photo) ? contact.Photo[0]?.url : contact.Photo;
                  const initials = contact.Name ? contact.Name.split(' ').map(n => n[0]).join('').toUpperCase() : '?';
                  const isMemberOfService = selectedServiceId === null ? true : getServiceContacts.some(m => m.id === contact.id);

                  // Determine availability status and color
                  const availability = (contact.Availability || '').toLowerCase().trim();
                  let availabilityColor = '#6366f1';
                  let availabilityLabel = contact.Availability || 'Available';

                  if (availability === 'available for work') {
                    availabilityColor = '#10b981';
                    availabilityLabel = 'Available for work';
                  } else if (availability === 'full load') {
                    availabilityColor = '#ef4444';
                    availabilityLabel = 'Full Load';
                  } else if (availability === 'part time') {
                    availabilityColor = '#f59e0b';
                    availabilityLabel = 'Part Time';
                  }

                  return (
                    <div
                      key={contact.id}
                      className={`member-card ${isMemberOfService ? 'in-service' : ''}`}
                      onClick={() => navigate(`/member/${contact.id}`, { state: { member: contact } })}
                    >
                      <div className="member-info-section">
                        <h3 className="member-name">{contact.Name}</h3>
                        {contact.Title && <p className="member-title">{contact.Title}</p>}
                        <div className="member-status">
                          <span className="status-badge"   style={{ 
    backgroundColor: availabilityColor,
    opacity: 0.8  // ← Add this line
  }}>
                            {availabilityLabel}
                          </span>
                        </div>
                      </div>

                      <div className="member-avatar-section">
                        <div
                          className="member-avatar"
                          style={{
                            borderColor: '#7a838bde',
                            //boxShadow: `0 0 0 3px ${availabilityColor}33`
                          }}
                        >
                          {imageUrl ? (
                            <img src={imageUrl} alt={contact.Name} className="member-image" />
                          ) : (
                            <div className="member-initials">{initials}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Load More Button */}
            {paginatedContacts.length > 0 && displayCount < filteredForAvailability.length && (
              <div className="load-more-container">
                <button
                  className="load-more-btn"
                  onClick={() => setDisplayCount(prev => prev + 12)}
                >
                  Load More
                </button>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Testimonials carousel - Fixed Swiper loop warning */}
      <section
        className="testimonials-section"
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL + '/images/hero/hero-background.jpg'})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <h2 className="services-title">Testimonials</h2>

        {tError && (
          <div className="services-error" role="alert">
            <strong>Error loading testimonials:</strong> {tError}
          </div>
        )}

        <Swiper
          modules={[Navigation, Pagination, Autoplay]}
          navigation
          pagination={{ clickable: true }}
          autoplay={{ delay: 4000, reverseDirection: true }}
          loop={testimonials?.length >= 3 ? true : false} // FIX: Only loop if enough slides
          spaceBetween={20}
          slidesPerView={1}
          breakpoints={{ 768: { slidesPerView: 2 }, 1024: { slidesPerView: 3 } }}
        >
          {tLoading && [0, 1].map(i => (
            <SwiperSlide key={`t-skel-${i}`}>
              <div className="testimonial-card skeleton">
                <div className="testimonial-overlay">
                  <div className="service-header">
                    <div className="service-icon skeleton-icon" />
                    <div className="service-name skeleton-line" style={{ width: '30%' }} />
                  </div>
                  <div className="service-description skeleton-line" style={{ width: '80%' }} />
                </div>
              </div>
            </SwiperSlide>
          ))}

          {!tLoading && Array.isArray(testimonials) && testimonials.map(item => {
            const { id, Name, Position, ImageUrl, Note } = item;
            const img = Array.isArray(ImageUrl) ? ImageUrl[0]?.url : ImageUrl;
            return (
              <SwiperSlide key={id}>
                <div className="testimonial-card">
                  <div className="testimonial-overlay" aria-hidden>
                    {img && <img src={img} alt={Name} loading="lazy" className="testimonial-avatar" />}
                    <div className="note-strip">
                      <div className="note-name">{Name}{Position ? ` — ${Position}` : ''}</div>
                      <div className="note-text">{Note}</div>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </section>
    </section>
  );
};

export default Services;